import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  House, Users, FileText, Bank, ArrowsLeftRight, 
  Repeat, SignOut, List, NotePencil, DownloadSimple, ListChecks, X
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: House },
  { path: '/clients', label: 'Clients', icon: Users },
  { path: '/letter-templates', label: 'Letter Templates', icon: NotePencil },
  { path: '/invoices', label: 'Invoices', icon: FileText },
  { path: '/bank-statements', label: 'Bank Statements', icon: Bank },
  { path: '/bank-feeds', label: 'Bank Feeds', icon: DownloadSimple },
  { path: '/matching', label: 'Matching', icon: ArrowsLeftRight },
  { path: '/recurring-invoices', label: 'Recurring Invoices', icon: Repeat },
  { path: '/form-templates', label: 'Forms', icon: ListChecks },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeSidebar} data-testid="sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:sticky top-0 left-0 h-screen z-50 md:z-auto
        w-64 border-r border-border bg-card flex flex-col
        transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary-foreground" weight="bold" />
            </div>
            <span className="font-semibold text-sm tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Applegrow
            </span>
          </div>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={closeSidebar} data-testid="close-sidebar">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-2 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                    isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                  }`
                }
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-5 h-5" weight="regular" />
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-3" data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <SignOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen w-full md:w-auto">
        {/* Top bar */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="sidebar-toggle">
            <List className="w-5 h-5" />
          </Button>
          <span className="text-sm text-muted-foreground md:hidden">Applegrow</span>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
